/**
 * Pattern-adapter registry for the element-insertion engine.
 *
 * Each adapter classifies a clicked element as a known CodeStitch repeat
 * pattern (social-link, nav-link, card, faq-row, button-group, ...). The
 * adapter declares its `itemSelector`, a `match(probe)` predicate, and a
 * `contentRecipe()` that returns the HTML for a fresh sibling.
 *
 * Adapters are registered in priority order — first registered wins. The
 * social-link adapter is registered first so a click on .cs-social-link
 * never gets swallowed by a more general "any repeat <a>" adapter.
 *
 * `match(probe)` runs against either:
 *   - a live DOM element (from a Node test, or from the parent's fallback
 *     bridge._iframe.contentDocument.querySelector), OR
 *   - a `chainProbe` synthesized from the iframe's postMessage payload's
 *     `ancestorChain`. Both shapes expose `.closest(selector)` so adapters
 *     can write `probe.closest('.cs-social-link')` without caring.
 */

const _adapters = [];

/**
 * Register an adapter. First registered wins on ties.
 * @param {object} adapter — must expose { kind, label, itemSelector, match, contentRecipe, menuActions, postInsertFlow, cssPatch }
 */
export function register(adapter) {
  if (!adapter || typeof adapter !== 'object') {
    throw new Error('register() requires an adapter object');
  }
  if (!adapter.kind) throw new Error('adapter.kind is required');
  if (typeof adapter.match !== 'function') {
    throw new Error(`adapter "${adapter.kind}" must export a match(probe) function`);
  }
  _adapters.push(adapter);
}

/**
 * Walk the registered adapters and return the first whose match(probe) is
 * truthy. Returns the adapter object (not the matched element) — the caller
 * already has the probe and can re-run `adapter.match(probe)` if they need
 * the repeat root.
 * @param {object} probe — a live DOM Element OR a chainProbe()
 * @returns {object|null}
 */
export function resolveAdapter(probe) {
  if (!probe) return null;
  for (const adapter of _adapters) {
    try {
      if (adapter.match(probe)) return adapter;
    } catch {
      // A buggy adapter must not poison the search.
    }
  }
  return null;
}

/**
 * Resolve a registered adapter by its `kind`. The linchpin of the registry-
 * driven reorder commit: element-actions.js uses this in _onReorder (to pick
 * the mover / history label / toast) AND in _itemSelectorFor (so duplicate /
 * delete / visibility resolve the correct repeat-root selector per kind).
 * @param {string} kind
 * @returns {object|null}
 */
export function getAdapterByKind(kind) {
  if (!kind) return null;
  for (const adapter of _adapters) {
    if (adapter && adapter.kind === kind) return adapter;
  }
  return null;
}

/**
 * Distinct history labels of all reorderable adapters. Used to DERIVE the
 * surgical-sync label allow-set (shell.js) so a new draggable kind lights up
 * without hand-editing that Set.
 * @returns {string[]}
 */
export function getReorderableHistoryLabels() {
  const labels = [];
  for (const adapter of _adapters) {
    if (
      adapter &&
      adapter.reorderable &&
      adapter.historyLabel &&
      labels.indexOf(adapter.historyLabel) === -1
    ) {
      labels.push(adapter.historyLabel);
    }
  }
  return labels;
}

/**
 * Return native CSS selectors for adapters that support direct reorder.
 * Iframe-side matching uses Element.matches(), so adapters may provide
 * stricter selectors than the chainProbe-compatible itemSelector.
 */
export function getReorderableSelectors() {
  return _adapters
    .filter((adapter) => adapter?.reorderable)
    .map((adapter) => adapter.reorderSelector || adapter.itemSelector)
    .filter(Boolean);
}

/**
 * Return source/sibling selector pairs for direct reorder controllers.
 *
 * `sourceSelector` answers "can this element be grabbed?" while
 * `siblingSelector` answers "does this top-level sibling participate in
 * hit-testing, gap animation, and before/after targets?" Most adapters use the
 * same selector for both; nav links keep dropdown roots as siblings without
 * making them drag handles.
 */
export function getReorderableSpecs() {
  return _adapters
    .filter((adapter) => adapter?.reorderable)
    .map((adapter) => {
      const sourceSelector = adapter.reorderSelector || adapter.itemSelector;
      const siblingSelector = adapter.reorderSiblingSelector || sourceSelector;
      if (!sourceSelector) return null;
      return {
        kind: adapter.kind,
        itemSelector: adapter.reorderItemSelector || adapter.itemSelector || sourceSelector,
        parentSelector: adapter.parentSelector || null,
        sourceSelector,
        siblingSelector,
        requiredAncestorSelector: adapter.requiredAncestorSelector || null,
        rejectIfTargetClosest: adapter.rejectIfTargetClosest || null,
        // Serializable reorder metadata for the iframe drag engine. `moveFn` /
        // `toastText` are intentionally NOT emitted (parent-only; functions
        // don't serialize). vtSelector defaults to siblingSelector.
        axis: adapter.reorderAxis || 'x',
        historyLabel: adapter.historyLabel || null,
        vtPrefix: adapter.vtPrefix || null,
        vtSelector: adapter.vtSelector || siblingSelector
      };
    })
    .filter(Boolean);
}

/** Test-only: clear the registry. */
export function _resetAdapters() {
  _adapters.length = 0;
}

/** Test-only: count registered adapters. */
export function _adapterCount() {
  return _adapters.length;
}

/**
 * Build a probe object from an `ancestorChain` payload (sent by the iframe
 * on contextmenu). The probe exposes a minimal subset of the DOM Element
 * API — currently just `closest(selector)` — sufficient for adapters that
 * match by class / tag.
 *
 * `chain` is an array of `{ tagName, classNames, eid? }` ordered from the
 * clicked element up to (but not including) the section root.
 */
export function chainProbe(chain) {
  if (!Array.isArray(chain) || chain.length === 0) return null;
  const normalized = chain.map((entry) => ({
    tagName: String(entry?.tagName || '').toLowerCase(),
    classNames: Array.isArray(entry?.classNames)
      ? entry.classNames.map((c) => String(c || '').toLowerCase()).filter(Boolean)
      : [],
    eid: entry?.eid || null,
    repeatItem: entry?.repeatItem === true
  }));

  function entryMatchesSimpleSelector(entry, selector) {
    const sel = String(selector || '').trim();
    if (!sel) return false;
    // Parse a simple compound selector: tagName.class1.class2 or just .class.
    let tag = '';
    let rest = sel;
    const dotIdx = rest.indexOf('.');
    if (dotIdx === -1) {
      tag = rest.toLowerCase();
      rest = '';
    } else if (dotIdx > 0) {
      tag = rest.slice(0, dotIdx).toLowerCase();
      rest = rest.slice(dotIdx);
    }
    if (tag && tag !== '*' && tag !== entry.tagName) return false;
    const classes = rest
      .split('.')
      .filter(Boolean)
      .map((c) => c.toLowerCase());
    for (const cls of classes) {
      if (!entry.classNames.includes(cls)) return false;
    }
    return true;
  }

  function entryMatchesSelectorAt(index, selector) {
    const sel = String(selector || '').trim();
    if (!sel) return false;
    // Comma-separated alternation: matches if any alternative matches.
    if (sel.includes(',')) {
      return sel.split(',').some((s) => entryMatchesSelectorAt(index, s.trim()));
    }
    // Descendant selector support for future adapters like `.cs-ul .cs-li`.
    const parts = sel.split(/\s+/).filter(Boolean);
    if (parts.length > 1) {
      if (!entryMatchesSimpleSelector(normalized[index], parts[parts.length - 1])) return false;
      let ancestorIndex = index + 1;
      for (let partIndex = parts.length - 2; partIndex >= 0; partIndex--) {
        let matched = false;
        while (ancestorIndex < normalized.length) {
          if (entryMatchesSimpleSelector(normalized[ancestorIndex], parts[partIndex])) {
            matched = true;
            ancestorIndex++;
            break;
          }
          ancestorIndex++;
        }
        if (!matched) return false;
      }
      return true;
    }
    return entryMatchesSimpleSelector(normalized[index], sel);
  }

  return {
    /** Return the first chain entry matching the selector, or null. */
    closest(selector) {
      for (let i = 0; i < normalized.length; i++) {
        if (entryMatchesSelectorAt(i, selector)) return normalized[i];
      }
      return null;
    },
    /** Convenience: the clicked-most entry. */
    get target() {
      return normalized[0];
    },
    /** Read-only chain access for adapters that want to inspect ancestors. */
    get chain() {
      return normalized;
    }
  };
}
